import {
  type Millicores,
  type Bytes,
  millicores,
  bytes,
} from "../units";
import {
  SUGGESTION_CONSTANTS,
  DEFAULT_OVERPROVISIONING_THRESHOLDS,
  EXCLUDED_NAMESPACES_FOR_OVERPROVISIONING,
  MIN_POD_AGE_MS,
  type OverprovisioningConfig,
} from "./constants";

// =============================================================================
// Helper Functions
// =============================================================================

/**
 * Round a value up to the nearest multiple of the given increment.
 * Always rounds up (ceiling) for safety.
 *
 * @example
 * roundToNiceValue(137, 10) // 140
 * roundToNiceValue(423, 16) // 432
 */
function roundToNiceValue(
  value: number,
  increment: number
): number {
  return Math.ceil(value / increment) * increment;
}

/**
 * Round CPU value to nice increments based on magnitude.
 * - < 100m: round to 10m (10, 20, 30...)
 * - >= 100m: round to 50m (100, 150, 200...)
 * - >= 1000m: round to 100m (1000, 1100, 1200...)
 */
function roundCpuToNiceValue(
  cpuMillicores: number
): number {
  if (cpuMillicores < 100) {
    return roundToNiceValue(cpuMillicores, 10);
  }
  if (cpuMillicores < 1000) {
    return roundToNiceValue(cpuMillicores, 50);
  }
  return roundToNiceValue(cpuMillicores, 100);
}

/**
 * Round memory value to nice increments based on magnitude.
 * - < 128Mi: round to 16Mi
 * - >= 128Mi: round to 64Mi
 * - >= 1Gi: round to 128Mi
 */
function roundMemoryToNiceValue(
  memoryBytes: number
): number {
  const MiB = 1024 * 1024;
  const memoryMiB = memoryBytes / MiB;

  if (memoryMiB < 128) {
    return roundToNiceValue(memoryBytes, 16 * MiB);
  }
  if (memoryMiB < 1024) {
    return roundToNiceValue(memoryBytes, 64 * MiB);
  }
  return roundToNiceValue(memoryBytes, 128 * MiB);
}

// =============================================================================
// Types
// =============================================================================

export type SuggestionInput = {
  cpuRequestMillicores: Millicores | null;
  cpuLimitMillicores: Millicores | null;
  memoryRequestBytes: Bytes | null;
  memoryLimitBytes: Bytes | null;
  cpuUsageMillicores: Millicores;
  memoryUsageBytes: Bytes;
  maxCpuUsageMillicores?: Millicores;
  maxMemoryUsageBytes?: Bytes;
  p95CpuUsageMillicores?: Millicores;
  p95MemoryUsageBytes?: Bytes;
  p99CpuUsageMillicores?: Millicores;
  p99MemoryUsageBytes?: Bytes;
};

/**
 * Result of suggested resource requests and limits calculation.
 */
export type SuggestionResult = {
  /** Suggested CPU request in millicores (null if no request set or no suggestion) */
  suggestedCpuRequestMillicores: Millicores | null;
  /** Suggested CPU limit in millicores (null if no limit set or no suggestion) */
  suggestedCpuLimitMillicores: Millicores | null;
  /** Suggested memory request in bytes (null if no request set or no suggestion) */
  suggestedMemoryRequestBytes: Bytes | null;
  /** Suggested memory limit in bytes (null if no limit set or no suggestion) */
  suggestedMemoryLimitBytes: Bytes | null;
  /** CPU usage percentage of current limit */
  cpuUsagePercent: number | null;
  /** Memory usage percentage of current limit */
  memoryUsagePercent: number | null;
  /** Potential CPU savings percentage (0-100) */
  cpuSavingsPercent: number | null;
  /** Potential memory savings percentage (0-100) */
  memorySavingsPercent: number | null;
  /** Whether historical data was used for calculation */
  usedHistoricalData: boolean;
};

/**
 * Result of over-provisioning check for a pod.
 */
export type OverprovisioningCheckResult = {
  /** Whether this pod should be checked for over-provisioning */
  shouldCheck: boolean;
  /** Reason why pod was skipped (if shouldCheck is false) */
  skipReason?: string;
  /** Whether CPU is over-provisioned (if checked) */
  isCpuOverprovisioned?: boolean;
  /** Whether memory is over-provisioned (if checked) */
  isMemoryOverprovisioned?: boolean;
  /** Suggestion result (if checked) */
  suggestion?: SuggestionResult;
};

// =============================================================================
// Calculation Functions
// =============================================================================

/**
 * Calculate suggested resource requests and limits based on usage.
 * Uses percentile/max historical usage for calculations.
 *
 * Formula:
 * - Request: max(ceil(p95Usage * requestHeadroomMultiplier), minimum)
 * - Limit: max(ceil(p99Usage * limitHeadroomMultiplier), minimum, effectiveRequest * limitToRequestRatio)
 *
 * Where effectiveRequest = suggestedRequest ?? currentRequest (the request we intend to apply).
 *
 * Guarantees:
 * - suggestedLimit >= effectiveRequest (K8s constraint when applying request+limit together)
 * - suggestedLimit >= effectiveRequest * limitToRequestRatio (headroom for bursts)
 *
 * Fallback hierarchy:
 * - For requests: p95 -> avg -> current usage
 * - For limits: p99 -> max -> current usage
 */
export function calculateSuggestedLimits(
  input: SuggestionInput
): SuggestionResult {
  const {
    cpuRequestMillicores,
    cpuLimitMillicores,
    memoryRequestBytes,
    memoryLimitBytes,
    cpuUsageMillicores,
    memoryUsageBytes,
    maxCpuUsageMillicores,
    maxMemoryUsageBytes,
    p95CpuUsageMillicores,
    p95MemoryUsageBytes,
    p99CpuUsageMillicores,
    p99MemoryUsageBytes,
  } = input;

  const usedHistoricalData =
    maxCpuUsageMillicores !== undefined ||
    maxMemoryUsageBytes !== undefined ||
    p95CpuUsageMillicores !== undefined ||
    p95MemoryUsageBytes !== undefined ||
    p99CpuUsageMillicores !== undefined ||
    p99MemoryUsageBytes !== undefined;

  // For requests: max(p95, current) - clamp to avoid suggesting below current usage
  const cpuForRequestSuggestion = Math.max(
    p95CpuUsageMillicores ?? cpuUsageMillicores,
    cpuUsageMillicores
  ) as Millicores;
  const memForRequestSuggestion = Math.max(
    p95MemoryUsageBytes ?? memoryUsageBytes,
    memoryUsageBytes
  ) as Bytes;

  // For limits: max(p99, max, current) - clamp to avoid suggesting below observed usage
  const cpuForLimitSuggestion = Math.max(
    p99CpuUsageMillicores ??
      maxCpuUsageMillicores ??
      cpuUsageMillicores,
    cpuUsageMillicores
  ) as Millicores;
  const memForLimitSuggestion = Math.max(
    p99MemoryUsageBytes ??
      maxMemoryUsageBytes ??
      memoryUsageBytes,
    memoryUsageBytes
  ) as Bytes;

  const cpuUsagePercent =
    cpuLimitMillicores !== null && cpuLimitMillicores > 0
      ? (cpuUsageMillicores / cpuLimitMillicores) * 100
      : null;
  const memoryUsagePercent =
    memoryLimitBytes !== null && memoryLimitBytes > 0
      ? (memoryUsageBytes / memoryLimitBytes) * 100
      : null;

  const suggestedCpuRequestMillicores =
    cpuRequestMillicores !== null
      ? millicores(
          roundCpuToNiceValue(
            Math.max(
              Math.ceil(
                cpuForRequestSuggestion *
                  SUGGESTION_CONSTANTS.requestHeadroomMultiplier
              ),
              SUGGESTION_CONSTANTS.minCpuRequestMillicores
            )
          )
        )
      : null;

  const suggestedMemoryRequestBytes =
    memoryRequestBytes !== null
      ? bytes(
          roundMemoryToNiceValue(
            Math.max(
              Math.ceil(
                memForRequestSuggestion *
                  SUGGESTION_CONSTANTS.requestHeadroomMultiplier
              ),
              SUGGESTION_CONSTANTS.minMemoryRequestBytes
            )
          )
        )
      : null;

  // effectiveRequest = request we intend to apply (allows limit reduction when also reducing request)
  const effectiveCpuRequest =
    suggestedCpuRequestMillicores ??
    cpuRequestMillicores ??
    0;
  const effectiveMemoryRequest =
    suggestedMemoryRequestBytes ?? memoryRequestBytes ?? 0;

  // Enforce: limit >= max(usage*headroom, minimum, effectiveRequest, effectiveRequest*ratio)
  const suggestedCpuLimitMillicores =
    cpuLimitMillicores !== null
      ? millicores(
          roundCpuToNiceValue(
            Math.max(
              Math.ceil(
                cpuForLimitSuggestion *
                  SUGGESTION_CONSTANTS.cpuHeadroomMultiplier
              ),
              SUGGESTION_CONSTANTS.minCpuLimitMillicores,
              // K8s constraint: limit >= effectiveRequest (the request we intend to apply)
              effectiveCpuRequest,
              // Headroom: limit >= effectiveRequest * ratio
              Math.ceil(
                effectiveCpuRequest *
                  SUGGESTION_CONSTANTS.limitToRequestRatio
              )
            )
          )
        )
      : null;

  const suggestedMemoryLimitBytes =
    memoryLimitBytes !== null
      ? bytes(
          roundMemoryToNiceValue(
            Math.max(
              Math.ceil(
                memForLimitSuggestion *
                  SUGGESTION_CONSTANTS.memoryHeadroomMultiplier
              ),
              SUGGESTION_CONSTANTS.minMemoryLimitBytes,
              effectiveMemoryRequest,
              Math.ceil(
                effectiveMemoryRequest *
                  SUGGESTION_CONSTANTS.limitToRequestRatio
              )
            )
          )
        )
      : null;

  const cpuSavingsPercent =
    cpuLimitMillicores !== null &&
    suggestedCpuLimitMillicores !== null
      ? Math.max(
          0,
          ((cpuLimitMillicores -
            suggestedCpuLimitMillicores) /
            cpuLimitMillicores) *
            100
        )
      : null;

  const memorySavingsPercent =
    memoryLimitBytes !== null &&
    suggestedMemoryLimitBytes !== null
      ? Math.max(
          0,
          ((memoryLimitBytes - suggestedMemoryLimitBytes) /
            memoryLimitBytes) *
            100
        )
      : null;

  return {
    suggestedCpuRequestMillicores,
    suggestedCpuLimitMillicores,
    suggestedMemoryRequestBytes,
    suggestedMemoryLimitBytes,
    cpuUsagePercent,
    memoryUsagePercent,
    cpuSavingsPercent,
    memorySavingsPercent,
    usedHistoricalData,
  };
}

/**
 * Calculate savings percentage between current and suggested limit.
 */
export function calculateSavingsPercent(
  currentLimit: number,
  suggestedLimit: number
): number {
  if (currentLimit <= 0) return 0;
  return Math.max(
    0,
    Math.round(
      ((currentLimit - suggestedLimit) / currentLimit) * 100
    )
  );
}

/**
 * Calculate savings threshold percentage from multiplier.
 * Used to display what savings % a given multiplier represents.
 *
 * Formula: savings = 1 - (1 / multiplier)
 *
 * @example
 * calculateSavingsThresholdFromMultiplier(2.0)  // 50 - "Alert if >50% savings possible"
 * calculateSavingsThresholdFromMultiplier(10.0) // 90 - "Alert if >90% savings possible"
 * calculateSavingsThresholdFromMultiplier(1.0)  // null - multiplier must be > 1
 *
 * The result approaches but never reaches 100% as multiplier → ∞
 */
export function calculateSavingsThresholdFromMultiplier(
  multiplier: number
): number | null {
  if (multiplier <= 1) return null;
  return Math.round((1 - 1 / multiplier) * 100);
}

/**
 * Get severity based on utilization percentage.
 */
export function getSeverityFromPercent(
  percent: number | null
): "critical" | "warning" | "info" | null {
  if (percent === null) return null;
  if (
    percent >
    DEFAULT_OVERPROVISIONING_THRESHOLDS.cpuUsagePercent
  )
    return null; // Not over-provisioned
  if (percent > 95) return "critical";
  if (percent > 80) return "warning";
  return "info";
}

/**
 * Get CSS color class for percentage display.
 */
export function getPercentageColorClass(
  percent: number | null
): string {
  if (percent === null) return "";
  if (percent > 95) return "text-destructive";
  if (percent > 80) return "text-yellow-600";
  return "text-muted-foreground";
}

// =============================================================================
// Over-provisioning Check
// =============================================================================

/**
 * Check if a pod should be considered for over-provisioning detection.
 * Returns whether to check and the reason if skipped.
 */
export function shouldCheckPodForOverprovisioning(params: {
  namespace: string;
  phase: string;
  controllerKind: string | null;
  createdAt: string | null;
  hasHistoricalData: boolean;
}): { shouldCheck: boolean; skipReason?: string } {
  const {
    namespace,
    phase,
    controllerKind,
    createdAt,
    hasHistoricalData,
  } = params;

  // Skip non-running pods
  if (phase !== "Running") {
    return {
      shouldCheck: false,
      skipReason: `Pod phase is ${phase}`,
    };
  }

  // Skip excluded namespaces
  if (
    EXCLUDED_NAMESPACES_FOR_OVERPROVISIONING.has(namespace)
  ) {
    return {
      shouldCheck: false,
      skipReason: `Namespace ${namespace} is excluded`,
    };
  }

  // Skip Job/CronJob pods (short-lived)
  if (
    controllerKind === "Job" ||
    controllerKind === "CronJob"
  ) {
    return {
      shouldCheck: false,
      skipReason: `${controllerKind} pods are short-lived`,
    };
  }

  // Skip pods younger than MIN_POD_AGE_MS when using historical data
  if (hasHistoricalData && createdAt) {
    const podAgeMs =
      Date.now() - new Date(createdAt).getTime();
    if (podAgeMs < MIN_POD_AGE_MS) {
      const hoursOld = Math.round(
        podAgeMs / (60 * 60 * 1000)
      );
      const minHours = Math.round(
        MIN_POD_AGE_MS / (60 * 60 * 1000)
      );
      return {
        shouldCheck: false,
        skipReason: `Pod is only ${hoursOld}h old (minimum ${minHours}h for reliable data)`,
      };
    }
  }

  return { shouldCheck: true };
}

/**
 * Check if a pod is over-provisioned based on usage and limits.
 * Applies hysteresis to prevent oscillation.
 */
export function checkOverProvisioning(
  suggestion: SuggestionResult,
  limits: {
    cpuLimitMillicores: Millicores | null;
    memoryLimitBytes: Bytes | null;
  },
  config: OverprovisioningConfig
): {
  isCpuOverprovisioned: boolean;
  isMemoryOverprovisioned: boolean;
} {
  const { cpuLimitMillicores, memoryLimitBytes } = limits;
  const {
    suggestedCpuLimitMillicores,
    suggestedMemoryLimitBytes,
    cpuUsagePercent,
    memoryUsagePercent,
  } = suggestion;

  // CPU check:
  // 1. Limit must be above minimum threshold
  // 2. Usage must be below threshold percentage
  // 3. Savings must be significant (hysteresis)
  const isCpuBelowThreshold =
    cpuLimitMillicores !== null &&
    cpuLimitMillicores > config.minCpuLimitMillicores &&
    cpuUsagePercent !== null &&
    cpuUsagePercent < config.cpuUsagePercent;

  const isCpuSavingsSignificant =
    cpuLimitMillicores !== null &&
    suggestedCpuLimitMillicores !== null &&
    cpuLimitMillicores >
      suggestedCpuLimitMillicores *
        config.savingsMultiplier;

  const isCpuOverprovisioned =
    isCpuBelowThreshold && isCpuSavingsSignificant;

  // Memory check (same logic)
  const isMemBelowThreshold =
    memoryLimitBytes !== null &&
    memoryLimitBytes > config.minMemoryLimitBytes &&
    memoryUsagePercent !== null &&
    memoryUsagePercent < config.memoryUsagePercent;

  const isMemSavingsSignificant =
    memoryLimitBytes !== null &&
    suggestedMemoryLimitBytes !== null &&
    memoryLimitBytes >
      suggestedMemoryLimitBytes * config.savingsMultiplier;

  const isMemoryOverprovisioned =
    isMemBelowThreshold && isMemSavingsSignificant;

  return { isCpuOverprovisioned, isMemoryOverprovisioned };
}
